/* eslint-disable */
import { node } from "~/util/dom";
import { DROPS } from "~/scene/provider/wasm/const";
import WASMProvider from "./provider";
import { getColorLineStyle, createMarker, createGeometry, removeItem, fixColor, setOpacity, updateGeometry, getIcon } from "./utils";
import SceneFeature from "~/scene/feature";
import SceneLayerMasakovac from "~/scene/layer/masakovac";
//import SceneLayer from "~/scene/layer/layer";

interface IMappingItem {
	wasm: any;
	scene: SceneFeature;
	type: "marker" | "geometry";
	hash: string;
}

const LAYER_MARKER_Z_INDEX = 150;
const LAYER_ACTIVE_Z_INDEX = 100;

export default class WASMVisualiser {
	_wasm: WASMProvider;

	_mapping: {[key: string | number]: IMappingItem};

	_postMarkers: Array<any>;

	_disabled: boolean;

	_layers: Array<any>;

	constructor(wasm: WASMProvider) {
		this._wasm = wasm;
		this._mapping = {};
		this._postMarkers = [];
		this._disabled = false;
		this._layers = [];
	}

	clearAllWasmContent() {
		Object.keys(this._mapping).forEach(markerId => {
			const item = this._mapping[markerId];

			if (item.type === "marker") {
				this._removeMarker(this._mapping[markerId].scene);
			} else if (item.type === "geometry") {
				this._removeGeometry(this._mapping[markerId].scene);
			}
		});
	}

	/**
	 * Vola se pri deaktivaci provideru.
	 */
	clear() {
		this.clearAllWasmContent();
	}

	addLayer(layer, before) {
		if (!this._layers.includes(layer)) {
			this._layers.push(layer);
			this._refreshLayer(layer);
		}
	}

	/**
	 * Odebrani vrstvy.
	 *
	 * @param {SceneLayer} layer Vrstva
	 */
	removeLayer(layer) {
		const ind = this._layers.indexOf(layer);

		if (ind !== -1) {
			this._layers.splice(ind, 1);
			this.layerClear(layer);
		}
	}

	runPost() {
		this._postMarkers.forEach(marker => this.createMarker(marker));
		this._postMarkers.length = 0;
	}

	/**
	 * Vytvoreni markeru.
	 *
	 * @param  {SceneMarker} marker
	 */
	createMarker(marker) {
		// jiz existuje
		if (this._getMappingItem(marker) || !marker.appended) return;

		const content = this._wasm.getContent();

		if (!content) {
			this._postMarkers.push(marker);
			return;
		}

		if (this._disabled) {
			return;
		}

		let fi = marker.featureInfo;
		let data = marker.getCreateData();
		let icon = "";
		let poiData = null;
		let alwaysVisible = false;

		// preskocime
		if (["SceneMarkerGEO"].indexOf(fi.name) !== -1) {
			return;
		}

		let iconId = DROPS.UNIVERSAL.id;

		switch (fi.name) {
			case "SceneMarkerPOI":
				if (data.fromData) {
					icon = getIcon(data.fromData);
					poiData = data.fromData;
				}
				break;

			case "SceneMarkerWindy":
				if (data.fromPOI) {
					icon = getIcon(data.fromPOI);
					poiData = data.fromPOI;
				} else {
					if (data.m3dData?.type === "route") {
						iconId = data.m3dData.value === "end"
							? DROPS.ROUTE_END.id
							: DROPS.ROUTE_START.id;
					}
				}
				break;

			case "SceneMarkerMapyWindy":
				if (data.fromData) {
					icon = getIcon(data.fromData);
					poiData = data.fromData;
				}
				else if (data.fromPOI) {
					icon = getIcon(data.fromPOI);
					poiData = data.fromPOI;
				}
				else if (data.options.icon) {
					icon = data.options.icon;
				} else {
					if (data.m3dData?.type === "route") {
						iconId = data.m3dData.value === "end"
							? DROPS.ROUTE_END.id
							: DROPS.ROUTE_START.id;
					}
				}

				iconId = DROPS.MEDIUM_RED_ICON.id;
				break;

			case "SceneMarkerMapyWindyDetail":
				if (data.fromPOI) {
					let opts = data.fromPOI.opts ? data.fromPOI.opts : { icon: "" };
					icon = opts.icon || getIcon(data.fromPOI.poi);
					poiData = data.fromPOI.poi;
				}

				iconId = icon
					? DROPS.LARGE_RED_ICON.id
					: DROPS.MEDIUM_RED_ICON.id;

				alwaysVisible = true;
				break;

			case "SceneMarkerMapyWindySearch":
				if (data.fromPOI) {
					icon = getIcon(data.fromPOI.poi);
					poiData = data.fromPOI.poi;

					if (data.fromPOI.opts && data.fromPOI.opts.alwaysVisible) {
						alwaysVisible = true;
					}
				}

				iconId = DROPS.MEDIUM_RED_ICON.id;
				break;

			case "SceneMarkerFotoPOI":
				icon = Mapy.CDN.setSize(data.fromData.url, Mapy.CDN.SIZE_40_40);

				if (data.fromData) {
					poiData = data.fromData;
				}
				break;

			case "SceneMarkerBubble":
				return;

			case "SceneMarker":
				if (data.m3dData?.type === "distancemeter") {
					iconId = DROPS.DISTANCEMETER.id;
				}
				break;
		}

		// souradnice
		let x = 0;
		let y = 0;

		if (data.coords) {
			x = data.coords.x;
			y = data.coords.y;
		}

		if (x === 0 && y === 0) return;

		const wasmEntity = createMarker({
			content,
			x,
			y,
			icon,
			iconId,
			data: poiData || {},
			onClick: () => {
				if (poiData) {
					Mapy.getComponent("detail").activate().setState({
						source: poiData.source,
						id: poiData.id,
						manual: true,
						show: false,
						search: Mapy.getComponent("detail").getSuggestConf()
					}).then(() => {
						Mapy.getComponent("history").save();
					});
				} else {
					// zkusime pouzit klik ze 3d nastaveni
					typeof data.m3dData?.click === "function" && data.m3dData.click();
				}
			},
		});

		this._saveEntity(marker, wasmEntity, "marker", "");
	}

	_createGeometry(content, lineCoords, lineStyle, zIndex) {
		const wasmEntity = createGeometry({
			content,
			lineCoords,
			lineStyle,
			zIndex,
		});

		return wasmEntity;
	}

	/**
	 * Vytvoreni geometrie.
	 *
	 * @param  {SceneGeometry} geometry
	 */
	createGeometry(geometry, zIndexArg = 0) {
		// jiz existuje
		if (this._getMappingItem(geometry) || !geometry.appended) return;

		const content = this._wasm.getContent();
		const coords = geometry.getCreateData().coords || [];

		if (!content) {
			console.log(["layerGeometryAddGeometry"]);
			return;
		}

		if (this._disabled || !coords.length) {
			return;
		}

		const options = geometry.getOptions();
		const isOptionsMulti = Array.isArray(options);
		const wasmItems = [];
		const DEF_STROKE = "#ff0000";
		const DEF_BORDER = "#ffffff";
		const DEF_OPACITY = 1;
		const hash = "";
		const topLevel = geometry.sceneOwner.getCreateData().options?.topLevel;
		const zIndex = topLevel
			? LAYER_ACTIVE_Z_INDEX
			: zIndexArg;

		//console.log(["create geometry", hash]);

		if (isOptionsMulti) {
			options.forEach((option, ind) => {
				const strokeOpacity = option.opacity || DEF_OPACITY;
				const strokeColor = setOpacity(fixColor(option.color || DEF_STROKE), strokeOpacity);
				const borderOpacity = option.outlineOpacity || DEF_OPACITY;
				const borderColor = setOpacity(fixColor(option.outlineColor || DEF_BORDER), borderOpacity);
				const lineStyle = getColorLineStyle(strokeColor, borderColor);
				const lineCoords = [];

				coords[ind].forEach(wgsPos => {
					lineCoords.push(wgsPos.x);
					lineCoords.push(wgsPos.y);
					lineCoords.push(wgsPos.x);
					lineCoords.push(wgsPos.y);
				});

				wasmItems.push(this._createGeometry(content, lineCoords, lineStyle, zIndex));
			});
		} else {
			const strokeOpacity = options.opacity || DEF_OPACITY;
			const strokeColor = setOpacity(fixColor(options.color || DEF_STROKE), strokeOpacity);
			const borderOpacity = options.outlineOpacity || DEF_OPACITY;
			const borderColor = setOpacity(fixColor(options.outlineColor || DEF_BORDER), borderOpacity);
			const lineStyle = getColorLineStyle(strokeColor, borderColor);
			const lineCoords = [];

			coords.forEach(wgsPos => {
				lineCoords.push(wgsPos.x);
				lineCoords.push(wgsPos.y);
				lineCoords.push(wgsPos.x);
				lineCoords.push(wgsPos.y);
			});

			wasmItems.push(this._createGeometry(content, lineCoords, lineStyle, zIndex));
		}

		this._saveEntity(geometry, wasmItems, "geometry", hash);
	}

	/**
	 * Udalost z feature - odstraneni entity.
	 *
	 * @param  {SceneFeature} sceneItem Volana entita
	 */
	featureDestroy(sceneItem) {
		this._removeMarker(sceneItem);
		this._removeGeometry(sceneItem);
	}

	/**
	 * Udalost z feature - ziskani DOM containeru
	 *
	 * @param  {SceneFeature} sceneItem Volana entita
	 * @param {Function} cb Callback funkce
	 * @param {Object} [scope = null] Vuci jakemu scopu volat funkci
	 */
	featureGetContainer(sceneItem, cb, scope) {
		let cont = node("div")

		if (cont && typeof cb === "function") {
			cb.apply(scope || cb, [cont]);
		}
	}

	/**
	 * Nastaveni souradnic u znacky.
	 *
	 * @param  {SceneMarker} marker Volany marker
	 * @param  {SceneCoords} coords Nove souradnice
	 */
	markerSetCoords(marker, coords) {
		this._refreshMarker(marker);
	}

	markerPOISetVisual(marker) {
		this._refreshMarker(marker);
	}

	/**
	 * Udalost z feature - ziskani DOM containeru, synchronni verze.
	 *
	 * @param  {SceneFeature} sceneItem Volana entita
	 */
	featureGetContainerSync(sceneItem) {
		return node("div");
	}

	/**
	 * Povoleni vrstvy.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 */
	layerEnable(layer) {
		if (this._layers.includes(layer)) {
			this._refreshLayer(layer);
		}
	}

	/**
	 * Zakazani vrstvy.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 */
	layerDisable(layer) {
		if (this._layers.includes(layer)) {
			this.layerClear(layer);
		}
	}

	/**
	 * Vycisteni vrstvy.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 */
	layerClear(layer) {
		if (!this._layers.includes(layer)) {
			return;
		}

		if (typeof layer.getMarkers === "function") {
			const markers = layer.getMarkers();

			markers.forEach(marker => this._removeMarker(marker));
		} else if (typeof layer.getGeometriesList === "function") {
			const geometries = layer.getGeometriesList();

			geometries.forEach(geom => this._removeGeometry(geom));
		}
	}

	/**
	 * Smazani vsech entit vrstvy.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 */
	layerRemoveAll(layer) {
		if (!this._layers.includes(layer)) {
			return;
		}

		this.layerClear(layer);
	}

	/**
	 * Prekresleni vrstvy.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param  {Boolean} [full] Plne prekresleni?
	 */
	layerRedraw(layer, full) {
		if (!layer.isActive() || !this._layers.includes(layer)) {
			return;
		}

		this._refreshLayer(layer);
	}

	/**
	 * Pridani markeru do vrstvy.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param {SceneMarker|Array[SceneMarker]} marker Vstupni markery
	 * @param {Boolean} noRedraw Neprekreslovat mapu
	 */
	layerMarkerAddMarkers(layer, markers, noRedraw) {
		if (!layer.isActive() || !this._layers.includes(layer)) {
			return;
		}

		markers.forEach(marker => {
			this.createMarker(marker);
		});
	}

	/**
	 * zmena na  makerech
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param {SceneMarker|Array[SceneMarker]} marker Vstupni markery
	 * @param {Boolean} noRedraw Neprekreslovat mapu
	 */
	layerMasakovacChangeMarkers(layer, markers, noRedraw) {
		this._refreshLayer(layer);
	}

	/**
	 * Odebrani markeru z vrstvy.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param {SceneMarker|Array[SceneMarker]} marker Vstupni markery
	 * @param {Boolean} noRedraw Neprekreslovat mapu
	 */
	layerMarkerRemoveMarkers(layer, markers, noRedraw) {
		if (!(layer instanceof SceneLayerMasakovac) && (!layer.isActive() || !this._layers.includes(layer))) {
			return;
		}

		markers.forEach(marker => this._removeMarker(marker));
	}

	_removeMarker(marker) {
		const data = this._getMappingItem(marker);

		if (data) {
			removeItem({
				content: this._wasm.getContent(),
				wasm: data.wasm,
			});
			delete this._mapping[data.scene.itemId];
		}
	}

	_removeGeometry(geom) {
		const data = this._getMappingItem(geom);

		if (data) {
			removeItem({
				content: this._wasm.getContent(),
				wasm: data.wasm,
			});
			delete this._mapping[data.scene.itemId];
		}
	}

	/**
	 * Vrstva Masakovac - pridani markeru.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param {string}  type     typ markeru dle this._TYPE_PRIORITY
	 * @param {SceneMarker}  marker   marker
	 * @param {string}  source   source bodu
	 * @param {integer} id       id bodu
	 * @param {boolean} noRedraw zda se nema po pridani bodu hnedka prekreslit (defaultne se prekresluje)
	 */
	layerMasakovacAddMarker(layer, type, marker, source, id, noRedraw) {
		this.createMarker(marker);
	}

	/**
	 * Vrstva Masakovac - odebrani markeru.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param  {SceneMarker|Array[SceneMarker]}  markers Markery
	 * @param  {boolean} noRedraw zda se nema po odebrani bodu prekreslit (defaultne se prekresluje)
	 * @param  {boolean} wasArray Pokud se vrati 1 item - bylo to volane s nim nebo to byl 1 prvek pole?
	 */
	layerMasakovacRemoveMarker(layer, markers, noRedraw, wasArray) {
		this.layerMarkerRemoveMarkers(layer, markers, noRedraw);
	}

	/**
	 * Vrstva Masakovac - odebere vsechny markery daneho typu.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param  {string} type typ markeru dle this._TYPE_PRIORITY
	 */
	layerMasakovacRemoveAll(layer, type) {
		const markers = layer.getMarkers(type);

		markers.forEach(marker => this._removeMarker(marker));
	}

	/**
	 * Vrstva s geometrii - pridani nove geometrie.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param  {SceneGeometry} geometry Geometrie k pridani
	 */
	layerGeometryAddGeometry(layer, geometry) {
		if (!layer.isActive() || !this._layers.includes(layer)) {
			return;
		}

		this.createGeometry(geometry);
	}

	/**
	 * Vrstva s geometrii - odebrani geometrie.
	 *
	 * @param  {SceneLayer} layer Volana vrstva
	 * @param  {SceneGeometry} geometry Geometrie k odstraneni
	 */
	layerGeometryRemoveGeometry(layer, geometry) {
		if (!layer.isActive() || !this._layers.includes(layer)) {
			return;
		}

		const data = this._getMappingItem(geometry);

		if (data) {
			//console.log(["aa", layer, geometry, data.wasm]);
			removeItem({
				content: this._wasm.getContent(),
				wasm: data.wasm,
			});
			delete this._mapping[data.scene.itemId];
		}
	}

	/**
	 * Nastaveni konfigurace u geometrie.
	 *
	 * @param  {SceneGeometry} geometry Volana geometrie
	 * @param  {Object} options Konfigurace
	 */
	geometrySetOptions(geometry, options) {
		this._refreshGeometry(geometry);
	}

	/**
	 * Vycisteni geometrie.
	 *
	 * @param  {SceneGeometry} geometry Volana geometrie
	 */
	geometryClear(geometry) {
		this._removeGeometry(geometry);
	}

	/**
	 * Nastaveni souradnic u geometrie.
	 *
	 * @param  {SceneGeometry} geometry Volana geometrie
	 * @param  {SceneCoords} coords Nove souradnice
	 */
	geometrySetCoords(geometry, coords) {
		const data = this._getMappingItem(geometry);

		if (data) {
			const content = this._wasm.getContent();
			const coords = geometry.getCreateData().coords || [];
			const lineCoords = [];

			coords.forEach(wgsPos => {
				lineCoords.push(wgsPos.x);
				lineCoords.push(wgsPos.y);
				lineCoords.push(wgsPos.x);
				lineCoords.push(wgsPos.y);
			});

			updateGeometry({
				content,
				lineCoords,
				lineId: data.wasm,
			});
		}
	}

	/**
	 * Ziskani mapovaci objektu.
	 *
	 * @param  {SceneFeature} sceneItem Objekt ze sceny
	 * @return {Object}
	 */
	_getMappingItem(sceneItem) {
		return (sceneItem.itemId in this._mapping ? this._mapping[sceneItem.itemId] : null);
	}

	/**
	 * Ulozeni smap entity, ktere je reprezentovana entitou nad scenou.
	 *
	 * @param  {SceneFeature} sceneItem Objekt ze sceny
	 * @param  {SMap Item} smapEntity Objekt nad mapou
	 */
	_saveEntity(sceneItem, wasmEntity, type: IMappingItem["type"], hash: IMappingItem["hash"]): IMappingItem {
		const obj = {
			wasm: Array.isArray(wasmEntity) ? wasmEntity : [wasmEntity],
			scene: sceneItem,
			type,
			hash,
		};

		this._mapping[sceneItem.itemId] = obj;

		return obj;
	}

	_refreshMarker(marker) {
		this._removeMarker(marker);
		this.createMarker(marker);
	}

	_refreshGeometry(geometry) {
		this._removeGeometry(geometry);
		this.createGeometry(geometry);
	}

	_refreshLayer(layer) {
		// smazeme
		this.layerClear(layer);

		// vytvorime
		if (typeof layer.getMarkers === "function") {
			const markers = layer.getMarkers();

			markers.forEach(marker => this.createMarker(marker));
		} else if (typeof layer.getGeometriesList === "function") {
			const geometries = layer.getGeometriesList();

			geometries.forEach((geom, ind) => this.createGeometry(geom, ind));
		}
	}
}
